import React, { useRef, useState } from 'react';

import { useTabEvents } from './DeprecatedTab.hooks';

import {
  Container,
  Icon,
  Panel,
  PanelContainer,
  Tab,
  TabGroup,
  TabGroupContainer,
  Text
} from './DeprecatedTabs.styles';
import { TabsProps } from './DeprecatedTabs.types';

export const DeprecatedTabs = ({
  options,
  size,
  name,
  offset,
  initialActiveTabId,
  activeTabId: controlledActiveTabId,
  onActiveTabChange,
  fixedHeightScrollContainer
}: TabsProps) => {
  const tabListRef = useRef<HTMLUListElement>(null);
  const [internalActiveTabId, setInternalActiveTabId] = useState(
    initialActiveTabId ? `tab-${initialActiveTabId}` : `tab-${options[0].id}`
  );
  const [onKeyDown] = useTabEvents(tabListRef);

  const activeTabId =
    controlledActiveTabId !== undefined ? `tab-${controlledActiveTabId}` : internalActiveTabId;

  const handleSetActiveTabId = (tabId: string) => {
    const rawId = tabId.replace(/^tab-/, '');
    if (controlledActiveTabId !== undefined) {
      onActiveTabChange?.(rawId);
    } else {
      setInternalActiveTabId(tabId);
    }
  };

  const isTabActive = (id: string) => id === activeTabId;

  return (
    <Container offset={offset}>
      <TabGroupContainer>
        <TabGroup role="tablist" aria-label={name} ref={tabListRef}>
          {options.map(tab => {
            const tabId = `tab-${tab.id}`;
            const panelId = `panel-${tab.id}`;

            return (
              <Tab
                id={tabId}
                key={tabId}
                $active={isTabActive(tabId)}
                size={size}
                tabIndex={0}
                role="tab"
                aria-controls={panelId}
                aria-selected={isTabActive(tabId)}
                onKeyDown={event => {
                  onKeyDown(event);

                  if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
                    return;
                  }

                  event.preventDefault();
                  handleSetActiveTabId(tabId);
                }}
                onClick={() => handleSetActiveTabId(tabId)}
              >
                {tab.icon && (
                  <Icon position={tab.icon.position} $active={isTabActive(tabId)} size={size}>
                    {tab.icon.component}
                  </Icon>
                )}
                <Text>{tab.label}</Text>
              </Tab>
            );
          })}
        </TabGroup>
      </TabGroupContainer>

      <PanelContainer scrollContainerHeight={fixedHeightScrollContainer}>
        {options.map(tab => {
          const tabId = `tab-${tab.id}`;
          const panelId = `panel-${tab.id}`;

          return (
            <Panel
              $active={isTabActive(tabId)}
              key={panelId}
              id={panelId}
              role="tabpanel"
              aria-labelledby={tabId}
            >
              {tab.panel}
            </Panel>
          );
        })}
      </PanelContainer>
    </Container>
  );
};
